export type ActionFunctionParamType = 'string' | 'mapping-list';

export interface ActionFunctionParamDefinition {
    type: ActionFunctionParamType;
    required?: boolean;
    minLength?: number;
    enum?: string[];
}

export interface ActionFunctionDefinition {
    params: Record<string, ActionFunctionParamDefinition>;
}

export interface ActionFunctionParamValidationIssue {
    path: string;
    message: string;
}

export interface ImportFromSchemaMapping {
    sourceAlias: string;
    targetField: string;
}

export interface ImportFromSchemaActionParams {
    /** Single-field form (legacy); equivalent to one mapping. */
    sourceAlias?: string;
    targetField?: string;
    /** Multi-field form: import several aliases into several fields at once. */
    mappings?: ImportFromSchemaMapping[];
    overwriteMode?: 'if-empty' | 'always';
    condition?: string;
}

export interface GenerateReportFieldActionParams {
    instruction: string;
    targetField: string;
    overwriteMode?: 'if-empty' | 'always';
    condition?: string;
}

export const ACTION_FUNCTION_DEFINITIONS: Record<string, ActionFunctionDefinition> = {
    copyNote: {
        params: {},
    },
    preview: {
        params: {},
    },
    saveDraft: {
        params: {},
    },
    completeReport: {
        params: {},
    },
    importFromSchema: {
        params: {
            // Either sourceAlias+targetField or a non-empty mappings list is
            // required (enforced in validateActionFunctionParams).
            sourceAlias: { type: 'string', minLength: 1 },
            targetField: { type: 'string', minLength: 1 },
            mappings: { type: 'mapping-list' },
            overwriteMode: {
                type: 'string',
                enum: ['if-empty', 'always'],
            },
            condition: { type: 'string' },
        },
    },
    generateReportField: {
        params: {
            instruction: { type: 'string', required: true, minLength: 1 },
            targetField: { type: 'string', required: true, minLength: 1 },
            overwriteMode: {
                type: 'string',
                enum: ['if-empty', 'always'],
            },
            condition: { type: 'string' },
        },
    },
};

export const getActionFunctionDefinition = (functionKey?: string) => {
    if (!functionKey) return undefined;
    return ACTION_FUNCTION_DEFINITIONS[functionKey];
};

const isObjectRecord = (value: unknown): value is Record<string, any> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

export const validateActionFunctionParams = ({
    functionKey,
    functionParams,
    path,
}: {
    functionKey?: string;
    functionParams: unknown;
    path: string;
}): ActionFunctionParamValidationIssue[] => {
    const definition = getActionFunctionDefinition(functionKey);
    if (!definition) {
        return [];
    }

    if (functionParams === undefined) {
        const missingRequired = Object.entries(definition.params).filter(
            ([, def]) => def.required
        );

        const missingIssues = missingRequired.map(([key]) => ({
            path: `${path}.${key}`,
            message: `functionParams.${key} is required`,
        }));
        if (functionKey === 'importFromSchema') {
            missingIssues.push({
                path: path.replace(/\.?$/, ''),
                message:
                    'importFromSchema requires sourceAlias + targetField or a non-empty mappings list',
            });
        }
        return missingIssues;
    }

    if (!isObjectRecord(functionParams)) {
        return [
            {
                path: path.replace(/\.?$/, ''),
                message: 'functionParams must be an object',
            },
        ];
    }

    const issues: ActionFunctionParamValidationIssue[] = [];
    const allowedKeys = new Set(Object.keys(definition.params));

    Object.keys(functionParams).forEach((paramKey) => {
        if (!allowedKeys.has(paramKey)) {
            issues.push({
                path: `${path}.${paramKey}`,
                message: `Unknown functionParams key "${paramKey}"`,
            });
        }
    });

    Object.entries(definition.params).forEach(([paramKey, paramDefinition]) => {
        const value = functionParams[paramKey];

        if (value === undefined || value === null) {
            if (paramDefinition.required) {
                issues.push({
                    path: `${path}.${paramKey}`,
                    message: `functionParams.${paramKey} is required`,
                });
            }
            return;
        }

        if (paramDefinition.type === 'mapping-list') {
            if (!Array.isArray(value)) {
                issues.push({
                    path: `${path}.${paramKey}`,
                    message: `functionParams.${paramKey} must be an array`,
                });
                return;
            }
            value.forEach((entry, index) => {
                const entryPath = `${path}.${paramKey}[${index}]`;
                if (!isObjectRecord(entry)) {
                    issues.push({
                        path: entryPath,
                        message: 'mapping must be an object',
                    });
                    return;
                }
                for (const key of ['sourceAlias', 'targetField'] as const) {
                    if (
                        typeof entry[key] !== 'string' ||
                        entry[key].trim().length === 0
                    ) {
                        issues.push({
                            path: `${entryPath}.${key}`,
                            message: `${key} is required`,
                        });
                    }
                }
            });
            return;
        }

        if (paramDefinition.type === 'string' && typeof value !== 'string') {
            issues.push({
                path: `${path}.${paramKey}`,
                message: `functionParams.${paramKey} must be a string`,
            });
            return;
        }

        if (
            typeof value === 'string' &&
            paramDefinition.minLength !== undefined &&
            value.trim().length < paramDefinition.minLength
        ) {
            issues.push({
                path: `${path}.${paramKey}`,
                message: `functionParams.${paramKey} must be at least ${paramDefinition.minLength} character(s)`,
            });
        }

        if (
            typeof value === 'string' &&
            paramDefinition.enum &&
            !paramDefinition.enum.includes(value)
        ) {
            issues.push({
                path: `${path}.${paramKey}`,
                message: `functionParams.${paramKey} must be one of: ${paramDefinition.enum.join(
                    ', '
                )}`,
            });
        }
    });

    if (functionKey === 'importFromSchema') {
        const hasPair =
            typeof functionParams.sourceAlias === 'string' &&
            functionParams.sourceAlias.trim().length > 0 &&
            typeof functionParams.targetField === 'string' &&
            functionParams.targetField.trim().length > 0;
        const hasMappings =
            Array.isArray(functionParams.mappings) &&
            functionParams.mappings.length > 0;
        if (!hasPair && !hasMappings) {
            issues.push({
                path: path.replace(/\.?$/, ''),
                message:
                    'importFromSchema requires sourceAlias + targetField or a non-empty mappings list',
            });
        }
    }

    return issues;
};
